import { useStore } from "../store"
import type SceneView from "@arcgis/core/views/SceneView"
import type Map from "@arcgis/core/Map"
import Camera from "@arcgis/core/Camera"
/**
 * Get Map object
 *
 * @return {Map}
 */
function getMap(): Map {
    return window.app.map
}
/**
 * Get Map object
 *
 * @return {Map}
 */
function getMapRight(): Map {
    return window.app.mapRight
}
/**
 * Get ScneView object
 *
 * @return {SceneView}
 */
function getView(): SceneView {
    return window.app.view
}
/**
 * Get ScneView object
 *
 * @return {SceneView}
 */
function getViewRight(): SceneView {
    return window.app.viewRight
}

function getDefaultCamera() {
    const DEFAULT_CAMERA = new Camera({
        position: {
            longitude: 14.421422530227927,
            latitude: 50.084298250001076,
            z: 325,
        },
        tilt: 69,
        heading: 357,
    })
    return DEFAULT_CAMERA
}

/**
 * Get sourceUrl form sources.json stored in store
 *
 * @param {String} sourceId - id of the source
 * @returns {String} sourceUrl string
 */
function getUrlFromSources(sourceId: string): string {
    const store = useStore()
    return store.sources[sourceId]
}

/**
 * Retrieves the ID from portal url from the sources in the store.
 *
 * @param sourceId - The ID of the source to retrieve the URL for.
 * @returns The URL associated with the given source ID.
 */
function getUrlFromSourcesById(sourceId: string): string {
    const store = useStore()
    const url = store.sources[sourceId]
    if (!url) {
        console.warn(`No URL found for source ID: ${sourceId}`)
        return ""
    }
    return store.sources[sourceId].split("id=")[1]
}

/**
 * Append path after a URL address
 * If a URL contains parameters like '?token=...', etc. the path is inserted before parameters
 *
 * @param {String} url - the url to be enhanced by path
 * @param {String} path - a url extension part
 * @returns {String} enhanced url
 */
function addPathToUrl(url: string, path: number | string): string {
    // check if the URL has parameters
    let params = ""
    const paramsIdx = url.indexOf("?")

    if (paramsIdx > -1) {
        params = url.substring(paramsIdx) // parameters only
        url = url.substring(0, paramsIdx) // url without parameters
    }

    // check if the url ends with '/' or not
    if (url[url.length - 1] !== "/") {
        url += "/" // if slash is missing, add it into the URL
    }

    return url + path + params
}

/**
 * Get value of URL paramater
 *
 * @param {String} name - name of the paramater
 * @param {Object} options - Object of options
 * @returns {String} values of the parameter
 */
export function gup(name: string, options?: { delete: boolean }): string {
    const url = new URL(window.location.href)
    const value = url.searchParams.get(name) ?? ""

    if (options?.delete) {
        deleteUrlParameter(name)
    }

    return value
}

/**
 * Change value of the paramater in the URL without reloading
 *
 * @param {String} parameter
 * @param {String} value
 * @returns {String} new URL string
 */
function changeUrlParameter(parameter: string, value: string): string {
    const url = window.location.href
    let newUrl = ""
    const hasOtherParams = url.includes("&")

    //check if paramater exists...if not then add it to the URL
    if (url.includes(parameter)) {
        if (hasOtherParams) {
            const beforeValuePart = url.split(parameter)[0]
            const afterValuePart = url.split(`${parameter}=${value}`)[1]
            newUrl = `${beforeValuePart}${parameter}=${value}${afterValuePart}`
        } else {
            const beforeValuePart = url.split("=")[0]
            newUrl = `${beforeValuePart}=${value}`
        }
    } else {
        if (hasOtherParams) {
            newUrl = `${url}&${parameter}=${value}`
        } else {
            newUrl = `${url}?${parameter}=${value}`
        }
    }
    window.history.pushState({}, "", newUrl)

    return newUrl
}

/**
 * Delete parameter and its value form the URL without reloading
 *
 * @param {String} parameter
 * @returns new URL
 */
export function deleteUrlParameter(parameter: string) {
    const url = window.location.href
    let newUrl = ""
    const urlObj = new URL(url)
    urlObj.searchParams.delete(parameter)
    newUrl = urlObj.toString()
    window.history.pushState({}, "", newUrl)
    return newUrl
}

export function useUtils() {
    return {
        getMap,
        getMapRight,
        getView,
        getViewRight,
        getDefaultCamera,
        getUrlFromSources,
        getUrlFromSourcesById,
        addPathToUrl,
        gup,
        changeUrlParameter,
        deleteUrlParameter,
    }
}
